<template>
    <div>
      <b-modal v-model="modalShow" centered cancel-disabled no-close-on-esc no-close-on-backdrop hide-header-close :title="getErrorTitle" hide-footer>
        <div v-if="!supportChatVisible" class="text-center">
          <div class="mx-auto error-img"><img width="96px" height="96px" :src="getErrorImg"/></div>
          <p :class="{ 'text-danger': !customError }" class="text-center" v-html="getErrorMsg"></p>
          <div v-if="!customError">
            <a @click="toggleShowDetailedError" class="clickable small">{{ showDetailedError ? $t('hide-detailed-error') : $t('show-detailed-error') }}</a>
            <div class="code-format mt-2" v-if="showDetailedError">
              Error: <b>{{ opentok.error.title }}</b><br>
              {{ opentok.error.msg }}
            </div>
          </div>
          <div class="col text-center pt-3" v-if="getCustomErrorBtnMsg !== ''">
            <b-button v-if="smallToken =! '' && !getCustomErrorBtnMsg" center v-on:click="pageReload" align-h="center" :variant="`outline-${getErrorColorVariant}`"><b-icon icon="arrow-counterclockwise" aria-hidden="true"></b-icon> {{ $t('retry') }}</b-button>
            <b-button v-if="getCustomErrorBtnMsg" center align-h="center" :variant="`outline-${getErrorColorVariant}`" v-on:click="doCustomErrorAction">
              {{ getCustomErrorBtnMsg }}
            </b-button>
          </div>
        </div>
        <div v-else>
          <hubspot-chat></hubspot-chat>
        </div>
        <div class="text-center mt-4">
          <b-button variant="outline-info" @click="$helpers.openSupportChat()" size="sm">{{ supportChatVisible ? $t('back') : $t('contact-by-chat-tech-problems') }}</b-button>
        </div>
      </b-modal>
    </div>
</template>

<script>
import { BIcon, BIconArrowCounterclockwise, BModal, BButton } from 'bootstrap-vue'
import HubspotChat from '@/components/public/HubspotChat'
import filters from '@/plugins/filters'
import * as Sentry from '@sentry/vue'
import { mapState } from 'vuex'

export default {
  name: 'Error',
  components: {
    BIcon,
    BIconArrowCounterclockwise,
    BModal,
    BButton,
    'hubspot-chat': HubspotChat
  },
  props: {
    customError: {
      type: String,
      required: false
    }
  },
  data () {
    return {
      modalShow: false,
      smallToken: '',
      showDetailedError: false,
      supportChatVisible: false
    }
  },
  created () {
    let logErrorMsg = this.customError
    if (this.opentok && this.opentok.error && this.opentok.error.title) {
      logErrorMsg = this.opentok.error.title
    }
    Sentry.captureMessage(`Error video: ${logErrorMsg}`)

    if (!this.customError) {
      this.$store.commit('video/setOpentokError', {
        title: this.$route.query.error_title,
        msg: this.$route.query.error_msg
      })
      this.$root.$emit('bv::show::modal', 'modal-1', '#btnShow')
      this.smallToken = this.$route.query.token
    } else {
      this.smallToken = this.$store.state.video.smallToken
    }
    this.modalShow = true
  },
  methods: {
    pageReload: function () {
      history.back()
    },
    toggleShowDetailedError () {
      this.showDetailedError = !this.showDetailedError
    },
    doCustomErrorAction () {
      switch (this.customError) {
        case 'VideoSessionFinishedException':
          this.$helpers.goToPatientWebApp(`appointments/preview/${this.appointment.id}`)
          break
        case 'VideoSessionFinishedWithReportException':
          this.$helpers.goToPatientWebApp(`appointments/report/${this.appointment.id}`)
          break
        case 'PaymentNotFoundException':
          this.$helpers.goToPatientWebApp(`appointments/pay/${this.appointment.id}/${this.appointment.price}`)
          break
        default:
          return null
      }
    }
  },
  computed: {
		...mapState('video', ['opentok', 'appointment']),
    getErrorTitle () {
      const i18nMsgKey = `errors.${this.customError}.title${this.$store.getters['session/userIsProfessional'] ? '_professional' : ''}`
      if (this.$te(i18nMsgKey)) {
        return this.$t(i18nMsgKey)
      } else {
        return this.$t('dialog-title-error')
      }
    },
    getErrorMsg () {
      const i18nMsgKey = !this.customError ? `errors.${this.opentok.error.title}` : `errors.${this.customError}.msg${this.$store.getters['session/userIsProfessional'] ? '_professional' : ''}`
      if (this.$te(i18nMsgKey) && !!this.appointment) {
        const appointmentDate = filters.beautifyDate(this.appointment.fromDateTime)
        return !this.customError ? this.$t(i18nMsgKey) : this.$t(i18nMsgKey, { date: appointmentDate })
      } else {
        return this.$t('errorVideoConnection')
      }
    },
    getErrorImg () {
      let img = this.customError
      switch (this.customError) {
        case 'VideoSessionFinishedException':
        case 'VideoSessionFinishedWithReportException':
        case 'VideoSessionWaitingReportException':
          img = 'VideoSessionFinishedException'
          break
        case 'VideoSessionExpiredException':
          img = 'VideoSessionNoLongerAccessibleException'
          break
        default:
          return '/static-appopensalud/img/icons/unplugged-icon-red-fill.svg'
      }
      return this.customError ? `/static-appopensalud/img/icons/errors/${img}.svg` : '/static-appopensalud/img/icons/unplugged-icon-red-fill.svg'
    },
    getCustomErrorBtnMsg () {
      switch (this.customError) {
        case 'VideoSessionFinishedException':
        case 'VideoSessionFinishedWithReportException':
          return this.$t('go-to-consultation')
        case 'PaymentNotFoundException':
          return this.$t('pay-consultation')
        case 'VideoSessionExpiredException':
        case 'VideoSessionNoLongerAccessibleException':
        case 'VideoSessionNotYetAccessibleException':
        case 'VideoSessionWaitingReportException':
          return ''
        default:
          return null
      }
    },
    getErrorColorVariant () {
      switch (this.customError) {
        case 'VideoSessionNoLongerAccessibleException':
        case 'VideoSessionNotYetAccessibleException':
        case 'VideoSessionWaitingReportException':
        case 'VideoSessionFinishedException':
        case 'VideoSessionExpiredException':
        case 'VideoSessionFinishedWithReportException':
        case 'PaymentNotFoundException':
          return 'primary'
        default:
          return 'danger'
      }
    }
  }
}
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style lang="scss">
h1,
h2 {
  font-weight: normal;
}

ul {
  list-style-type: none;
  padding: 0;
}

li {
  display: inline-block;
  margin: 0 10px;
}

a {
  color: #35495e;
}

.code-format {
  display: block;
  font-family: 'Source Code Pro', monospace;
  background-color: lightgray;
  border: 1px solid gray;
  font-size: 12px;
  padding: 10px;
}

.error-img {
  width: 96px;
  margin: 20px 0 40px 0;
}
</style>



// WEBPACK FOOTER //
// src/components/video-launcher/Error.vue